import { Controller, useForm } from 'react-hook-form';
import { ApolloCache } from '@apollo/client';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateResultRuleAction,
  EstateResultRuleAction_GetEstateResultRuleActionByEstateResultRuleIdDocument,
  EstateResultRuleAction_GetEstateResultRuleActionByEstateResultRuleIdQuery,
  EstateResultRuleActionType,
  useEstateResultRuleAction_UpdateEstateResultRuleActionMutation,
} from 'queries';

import FormSelectField from 'common/form-select-field';
import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

type Props = {
  estateResultRuleAction: EstateResultRuleAction;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onExitClick: Function;
};

const useStyles = makeStyles(() => ({
  h5: {
    paddingTop: '5px',
  },
  formSection: {
    paddingBottom: '10px',
  },
  form: {
    textAlign: 'left',
  },
  inputGrid: {
    paddingRight: '20px',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  container: {
    paddingTop: '10px',
  },
}));

type EstateResultRuleFormValues = {
  id: string;
  type: EstateResultRuleActionType;
  newResultItemHeader?: string;
  newResultItemSubheader?: string;
};

export default function EstateResultRuleActionForm({ estateResultRuleAction, onExitClick }: Props) {
  const classes = useStyles();

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
  } = useForm<EstateResultRuleFormValues>({
    values: {
      id: estateResultRuleAction.id,
      type: estateResultRuleAction.type,
      newResultItemHeader: estateResultRuleAction.newResultItemHeader || '',
      newResultItemSubheader: estateResultRuleAction.newResultItemSubheader || '',
    },
  });

  const type = watch('type');
  const showNewResultItemFields = type === EstateResultRuleActionType.NewResultItem;

  const readEstateResultRuleActionQuery = (
    store: ApolloCache<EstateResultRuleAction_GetEstateResultRuleActionByEstateResultRuleIdQuery>,
  ): EstateResultRuleAction_GetEstateResultRuleActionByEstateResultRuleIdQuery | null => {
    return store.readQuery<EstateResultRuleAction_GetEstateResultRuleActionByEstateResultRuleIdQuery>(
      {
        query: EstateResultRuleAction_GetEstateResultRuleActionByEstateResultRuleIdDocument,
      },
    );
  };

  const writeEstateResultRuleActionQuery = (
    store: ApolloCache<EstateResultRuleAction_GetEstateResultRuleActionByEstateResultRuleIdQuery>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ruleAction: any,
  ) => {
    store.writeQuery<EstateResultRuleAction_GetEstateResultRuleActionByEstateResultRuleIdQuery>({
      query: EstateResultRuleAction_GetEstateResultRuleActionByEstateResultRuleIdDocument,
      data: {
        data: ruleAction,
      },
    });
  };

  const [update, { loading: updateExecuting }] =
    useEstateResultRuleAction_UpdateEstateResultRuleActionMutation({
      update: (store) => {
        const ruleActionData = readEstateResultRuleActionQuery(store);
        if (ruleActionData) {
          writeEstateResultRuleActionQuery(store, ruleActionData.data);
        }
        onExitClick();
      },
      onError: (e) =>
        showAlert({
          message: `Error updating estate result rule action: ${e.message}`,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const submitForm = async (input: EstateResultRuleFormValues) => {
    update({
      variables: {
        input,
      },
    });
  };

  return (
    <>
      <Typography variant="h5" className={classes.h5}>
        Estate Result Rule Action Details
      </Typography>
      <form
        id="estate-result-rule-action-form"
        className={classes.form}
        onSubmit={handleSubmit(submitForm)}
      >
        <div className={classes.formSection}>
          <Grid container className={classes.container}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="type"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    options={[
                      { label: 'None', value: EstateResultRuleActionType.None },
                      {
                        label: 'New Result Item',
                        value: EstateResultRuleActionType.NewResultItem,
                      },
                    ]}
                    label="Type"
                    errorMessage={errors.type?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          {showNewResultItemFields && (
            <>
              <Grid container className={classes.container}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="newResultItemHeader"
                    control={control}
                    render={({ field: { ref, ...field } }) => (
                      <FormTextField
                        {...field}
                        inputRef={ref}
                        errorMessage={errors.newResultItemHeader?.message}
                        label="New Result Item Header"
                        inputProps={{ maxLength: 80 }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container className={classes.container}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="newResultItemSubheader"
                    control={control}
                    render={({ field: { ref, ...field } }) => (
                      <FormTextField
                        {...field}
                        inputRef={ref}
                        errorMessage={errors.newResultItemSubheader?.message}
                        label="New Result Item Subheader"
                        inputProps={{ maxLength: 80 }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Button
                type="submit"
                form="estate-result-rule-action-form"
                color="secondary"
                variant="contained"
                disabled={isSubmitting || updateExecuting}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </div>
      </form>
    </>
  );
}
